import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import useOutsideClick from '../../service/hooks';
import { fetchNotifications } from '../../redux/actions/Notifications';
import {
  getNotificationsItems,
  getNotificationsLoading,
} from '../../redux/selector/Notifications';
import { isAuth } from '../../redux/selector/User';
import { getMyAllSquad } from '../../redux/selector/Team';
import {
  StyledBellWrap,
  StyledBellButton,
  StyledBellDot,
  StyledBellPanel,
  StyledBellEmpty,
  StyledBellItem,
  StyledBellText,
  StyledBellTime,
} from './styled';

const SEEN_KEY = 'tol-notifications-seen';

const BellIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

export default function NotificationBell() {
  const dispatch = useDispatch();
  const history = useHistory();
  const isAuthenticated = useSelector(isAuth);
  const items = useSelector(getNotificationsItems);
  const loading = useSelector(getNotificationsLoading);
  const squad = useSelector(getMyAllSquad) || [];
  const [open, setOpen] = useState(false);
  const [seenIds, setSeenIds] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'));
    } catch {
      return new Set();
    }
  });
  const ref = useRef(null);
  useOutsideClick(ref, () => setOpen(false));

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    dispatch(fetchNotifications());
    const poll = () => {
      if (document.visibilityState === 'hidden') return;
      dispatch(fetchNotifications({ silent: true }));
    };
    const id = setInterval(poll, 45000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [dispatch, isAuthenticated]);

  if (!isAuthenticated) return null;

  const unread = items.filter((item) => !item.read && !seenIds.has(item.id));

  const openPanel = () => {
    setOpen((value) => !value);
    if (!open && items.length) {
      const next = new Set(seenIds);
      items.forEach((item) => next.add(item.id));
      setSeenIds(next);
      localStorage.setItem(SEEN_KEY, JSON.stringify([...next]));
    }
  };

  const openNotification = (item) => {
    setOpen(false);
    // Type 3 = set team / strategy. Type 4 = player details (link = player id).
    // Card/ban/captain Chris DMs are about own players — prefer strategy when
    // the squad isn't loaded yet (cold start from a push).
    if (item.type === 3) {
      history.push('/team/strategy');
      return;
    }
    if (item.type === 4 && item.link) {
      const own =
        !squad.length ||
        squad.some((player) => String(player._id) === String(item.link));
      if (own) {
        history.push('/team/strategy');
      } else {
        history.push('/transfers/search');
      }
    }
  };

  return (
    <StyledBellWrap ref={ref}>
      <StyledBellButton
        type="button"
        aria-label="Notifications"
        aria-expanded={open}
        onClick={openPanel}
      >
        <BellIcon />
        {unread.length > 0 ? <StyledBellDot aria-hidden /> : null}
      </StyledBellButton>
      {open ? (
        <StyledBellPanel role="dialog" aria-label="Notifications">
          {loading && items.length === 0 ? (
            <StyledBellEmpty>Loading…</StyledBellEmpty>
          ) : items.length === 0 ? (
            <StyledBellEmpty>No notifications</StyledBellEmpty>
          ) : (
            items.map((item) => {
              const clickable = item.type === 3 || (item.type === 4 && item.link);
              return (
                <StyledBellItem
                  key={item.id}
                  as={clickable ? 'button' : 'div'}
                  type={clickable ? 'button' : undefined}
                  onClick={clickable ? () => openNotification(item) : undefined}
                  style={clickable ? { cursor: 'pointer', textAlign: 'left', width: '100%', border: 0, background: 'transparent' } : undefined}
                >
                  <StyledBellText>{item.text}</StyledBellText>
                  {item.createdAt ? (
                    <StyledBellTime>
                      {moment(item.createdAt).fromNow()}
                    </StyledBellTime>
                  ) : null}
                </StyledBellItem>
              );
            })
          )}
        </StyledBellPanel>
      ) : null}
    </StyledBellWrap>
  );
}
